import store from 'config/store'
import * as actions from 'modules/session/actions'
import { pathOr } from 'ramda'
import {
  DRIFT_EXTERNAL_ID_COOKIE,
  DRIFT_EXTERNAL_ID_COOKIE_V1,
  DRIFT_USER_JWT_COOKIE
} from 'shared/constants/cookies'
import { SignedIdentityRequiredStatus } from 'types/embed'
import { log } from 'utils'
import { clearCookie, getCookieDomain, getCookieValue, setCookie, setPersistedCookie } from 'utils/cookies'
import { getExternalIdFromJwt, isJwtExpired, jwtIdMatchesExternalId } from 'utils/jwt'

const getExternalId = () => {
  const { session } = store.getState()
  return pathOr(null, ['externalId'], session)
}

const getExternalIdFromCookie = async () => {
  const externalIdFromV1Widget = await getCookieValue(DRIFT_EXTERNAL_ID_COOKIE_V1)
  const externalIdFromCookie = await getCookieValue(DRIFT_EXTERNAL_ID_COOKIE)

  if (externalIdFromV1Widget) {
    setCookie(DRIFT_EXTERNAL_ID_COOKIE, externalIdFromV1Widget)
    return externalIdFromV1Widget
  }

  return !!externalIdFromCookie ? externalIdFromCookie : null
}

const setExternalIdCookie = (externalId) => {
  setCookie(DRIFT_EXTERNAL_ID_COOKIE, externalId)
}

const clearExternalId = async () => {
  store.dispatch(actions.clearExternalId())
  await clearCookie(DRIFT_EXTERNAL_ID_COOKIE)

  if (!!getCookieDomain()) {
    await clearCookie(DRIFT_EXTERNAL_ID_COOKIE, { domain: '' })
  }
}

const getUserJwt = () => {
  const { session } = store.getState()
  return pathOr(undefined, ['endUser', 'userJwt'], session)
}

const getUserJwtFromCookie = async () => {
  const userJwtFromCookie = await getCookieValue(DRIFT_USER_JWT_COOKIE)
  if (!!userJwtFromCookie) {
    if (isJwtExpired(userJwtFromCookie)) {
      await clearUserJwtCookie()
    } else {
      return userJwtFromCookie
    }
  }

  return undefined
}

const clearUserJwtCookie = async () => {
  await clearCookie(DRIFT_USER_JWT_COOKIE)

  if (!!getCookieDomain()) {
    await clearCookie(DRIFT_USER_JWT_COOKIE, { domain: '' })
  }
}

const setUserJwtCookie = (useJwt) => {
  setPersistedCookie(DRIFT_USER_JWT_COOKIE, useJwt)
}

const clearUserJwt = async () => {
  store.dispatch(actions.clearExternalJwt())
  await clearUserJwtCookie()
}

export const getEmail = (): string => {
  const { session } = store.getState()
  return pathOr('', ['endUser', 'email'], session)
}

export const getExternalEmail = (): string => {
  const { session } = store.getState()
  return pathOr('', ['endUser', 'externalEmail'], session)
}


export const resolveExternalIdAndUserJwt = async () => {
  const didIdentifyWithoutJwt = getExternalId() && !getUserJwt()

  if (didIdentifyWithoutJwt) {
    const externalIdFromIdentify = getExternalId()
    const userJwtFromCookie = await getUserJwtFromCookie()
    if (jwtIdMatchesExternalId(userJwtFromCookie, externalIdFromIdentify)) {
      // Prefer getting data from more secure JWT, so discard externalId.
      clearExternalId()
    } else if (userJwtFromCookie) {
      clearUserJwt()
    }
  }

  let externalId = null
  let userJwt = null

  const resolvedUserJwt = getUserJwt() || await getUserJwtFromCookie()
  if (resolvedUserJwt && !isJwtExpired(resolvedUserJwt)) {
    userJwt = resolvedUserJwt
  }

  if (userJwt) {
    setUserJwtCookie(userJwt)
    externalId = getExternalIdFromJwt(userJwt)
  } else {
    externalId = getExternalId() || await getExternalIdFromCookie()
    if (externalId) {
      setExternalIdCookie(externalId)
      clearUserJwt()
    }
  }

  return {
    externalId,
    userJwt
  }
}

export const isWaitingForUserJwt = async () => {
  const jwtRequired = isSignedIdentityRequired()

  if (jwtRequired) {
    const { session = {} } = store.getState()

    const waitForUserJwt = pathOr(false, ['endUser', 'waitForUserJwt'], session)
    const { externalId, userJwt } = await resolveExternalIdAndUserJwt()
    return waitForUserJwt && !userJwt && !!externalId
  }

  return false
}

export const isSignedIdentityRequired = (): boolean => {
  const { embed = {} } = store.getState()
  const signedIdentityRequiredStatus = pathOr<SignedIdentityRequiredStatus>(
    SignedIdentityRequiredStatus.DISABLED,
    ['configuration', 'theme', 'signedIdentityRequiredStatus'],
    embed
  )
  return signedIdentityRequiredStatus === SignedIdentityRequiredStatus.REQUIRED
}

export const getAuthForSocketConnection = async ({
  socketCluster,
  token,
  fn
}) => {
  const auth = pathOr(
    null,
    ['session', 'auth', 'socket', `${socketCluster}`],
    store.getState()
  )

  if (auth) {
    return auth
  }

  const { data } = await fn({ access_token: token })

  if (!data) {
    log({
      type: 'warn',
      data: [`Empty payload response from ${socketCluster} socket auth`]
    })
    return
  }

  return data
}
